import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, ExternalLink, GripVertical } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const emptyForm = { name: "", slug: "", description: "", icon: "", external_link: "", order: 0 };

export default function RubriqueManager() {
  const [rubriques, setRubriques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try { const res = await api.get("/rubriques"); setRubriques(res.data); } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Le nom est requis");
    try {
      if (editId) {
        await api.put(`/rubriques/${editId}`, form);
        toast.success("Rubrique mise à jour");
      } else {
        await api.post("/rubriques", form);
        toast.success("Rubrique créée");
      }
      setOpen(false);
      setForm(emptyForm);
      setEditId(null);
      load();
    } catch { toast.error("Erreur"); }
  };

  const handleEdit = (rub) => {
    setForm({ name: rub.name, slug: rub.slug, description: rub.description || "", icon: rub.icon || "", external_link: rub.external_link || "", order: rub.order || 0 });
    setEditId(rub.id);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette rubrique ?")) return;
    try { await api.delete(`/rubriques/${id}`); toast.success("Rubrique supprimée"); load(); } catch { toast.error("Erreur"); }
  };

  const handleNew = () => {
    setForm(emptyForm);
    setEditId(null);
    setOpen(true);
  };

  return (
    <div data-testid="rubrique-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl">Rubriques</h1>
          <p className="text-muted-foreground mt-1">Gérez les rubriques du blog (Lives, Vidéos, Articles, etc.)</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-none" onClick={handleNew} data-testid="new-rubrique-btn">
              <Plus className="h-4 w-4 mr-2" /> Nouvelle rubrique
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">{editId ? "Modifier la rubrique" : "Nouvelle rubrique"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Nom</Label>
                <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="rounded-none mt-1" placeholder="Ex: Lives d'OFA" data-testid="rubrique-name-input" />
              </div>
              <div>
                <Label>Slug (optionnel)</Label>
                <Input value={form.slug} onChange={(e) => setForm(f => ({ ...f, slug: e.target.value }))} className="rounded-none mt-1 font-mono text-sm" placeholder="Auto-généré si vide" data-testid="rubrique-slug-input" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} className="rounded-none mt-1" placeholder="Description courte" data-testid="rubrique-desc-input" />
              </div>
              <div>
                <Label>Lien externe (optionnel)</Label>
                <Input value={form.external_link} onChange={(e) => setForm(f => ({ ...f, external_link: e.target.value }))} className="rounded-none mt-1" placeholder="https://... (laissez vide pour une page interne)" data-testid="rubrique-link-input" />
                <p className="text-xs text-muted-foreground mt-1">Si rempli, cette rubrique redirigera vers ce lien (ex: Quiz vers Canvas)</p>
              </div>
              <div>
                <Label>Ordre d'affichage</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm(f => ({ ...f, order: parseInt(e.target.value) || 0 }))} className="rounded-none mt-1 w-24" data-testid="rubrique-order-input" />
              </div>
              <Button className="rounded-none w-full" onClick={handleSave} data-testid="save-rubrique-btn">
                {editId ? "Mettre à jour" : "Créer"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border rounded-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead>Nom</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Articles</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8">Chargement...</TableCell></TableRow>
              ) : rubriques.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Aucune rubrique</TableCell></TableRow>
              ) : rubriques.map((rub) => (
                <TableRow key={rub.id}>
                  <TableCell className="text-muted-foreground text-sm">{rub.order}</TableCell>
                  <TableCell className="font-medium">{rub.name}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{rub.slug}</TableCell>
                  <TableCell>
                    {rub.external_link ? (
                      <Badge variant="outline" className="rounded-none text-xs">
                        <ExternalLink className="h-3 w-3 mr-1" /> Externe
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="rounded-none text-xs">Interne</Badge>
                    )}
                  </TableCell>
                  <TableCell>{rub.article_count || 0}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(rub)} data-testid={`edit-rubrique-${rub.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(rub.id)} data-testid={`delete-rubrique-${rub.id}`}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
